'use client'
import React, { useState, useEffect } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../@/components/ui/select";
import { Textarea } from "../../@/components/ui/textarea"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { ClipLoader } from "react-spinners";
import { useAuth } from "@clerk/clerk-react"
import {
    Field,
    FieldContent,
    FieldError,
    FieldLabel,
} from "../../@/components/ui/field";
import { cn } from "@/lib/utils";

const editPathwaySchema = z.object({
    title: z.string().min(1, "This is required"),
    level: z.string().min(1, "This is required"),
    levelId: z
        .string()
        .min(1, "This is required")
        .transform((v) => Number(v))
        .refine((n) => Number.isFinite(n) && n > 0, "This is required"),
    courses: z.array(z.string()).min(1, "This is required"),
    description: z.string().min(1, "This is required"),
})

export function EditPathwayDialog({ pathway, open, onOpenChange, onEdit, refetchPathways }) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [courses, setCourses] = useState([])
    const [isLoadingCourses, setIsLoadingCourses] = useState(false)
    const [pathwayTypes, setPathwayTypes] = useState([])
    const [isLoadingPathwayTypes, setIsLoadingPathwayTypes] = useState(false)
    const { getToken } = useAuth();

    const form = useForm({
        resolver: zodResolver(editPathwaySchema),
        defaultValues: {
            title: pathway?.name || '',
            level: pathway?.type || pathway?.category || '',
            levelId: String(pathway?.level_id ?? pathway?.levelId ?? ''),
            courses: pathway?.courses ? pathway.courses.map(String) : [],
            description: pathway?.description || '',
        }
    })

    const watchedSelectedCourses = form.watch("courses") ?? [];
    const watchedLevelId = form.watch("levelId") ?? "";
    const canSubmit = watchedSelectedCourses.length > 0 && String(watchedLevelId).trim().length > 0;

    useEffect(() => {
        if (open && courses.length === 0) {
            fetchCourses();
        }
        if (open && pathwayTypes.length === 0) {
            fetchPathwayTypes();
        }
        if (pathway) {
            form.reset({
                title: pathway.name || '',
                level: pathway.type || pathway.category || '',
                levelId: String(pathway?.level_id ?? pathway?.levelId ?? ''),
                courses: pathway.courses ? pathway.courses.map(String) : [],
                description: pathway.description || '',
            })
        }
        // eslint-disable-next-line
    }, [open, pathway])

    // If the pathway doesn't carry level_id but has a level name, infer id from fetched types.
    useEffect(() => {
        if (!open) return;
        if (!pathway) return;
        if (!Array.isArray(pathwayTypes) || pathwayTypes.length === 0) return;

        const currentLevelId = String(form.getValues("levelId") ?? "").trim();
        if (currentLevelId) return;

        const currentLevelName = String(form.getValues("level") ?? "").trim();
        if (!currentLevelName) return;

        const match = pathwayTypes.find((pt) => String(pt?.name || "").toLowerCase() === currentLevelName.toLowerCase());
        if (match?.id != null) {
            form.setValue("levelId", String(match.id), { shouldValidate: true });
        }
    }, [open, pathway, pathwayTypes, form]);

    const fetchCourses = async () => {
        try {
            setIsLoadingCourses(true)
            const response = await axios.get(api_endpoint + "/course/")
            if (response.data && response.data.courses) {
                setCourses(response.data.courses)
            } else {
                setCourses([])
            }
        } catch (error) {
            setCourses([])
            toast.error('Failed to load courses')
        } finally {
            setIsLoadingCourses(false)
        }
    }

    const fetchPathwayTypes = async () => {
        try {
            setIsLoadingPathwayTypes(true)

            const token = await getToken();
            if (!token) throw new Error("No authentication token found")

            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            };

            const response = await axios.get(api_endpoint + "/pathway/levels", reqConfig)
            const levels = response.data?.levels || []
            setPathwayTypes(levels)
        } catch (error) {
            setPathwayTypes([])
            toast.error('Failed to load pathway types')
        } finally {
            setIsLoadingPathwayTypes(false)
        }
    }

    const onSubmit = async (values) => {
        try {
            const token = await getToken();
            if (!token) return;
            
            setIsSubmitting(true);
            const payload = {
                title: values.title,
                description: values.description || "Not Available",
                level: values.level,
                level_id: values.levelId,
                courses: Array.isArray(values.courses)
                    ? values.courses.map(id => Number(id))
                    : [],
            };
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            };
            // Use the same endpoint as in ManagePathways
            const response = await axios.put(api_endpoint + `/pathway/edit/${pathway.id}`, payload, reqConfig);
            toast.success("Pathway updated successfully");
            if (onEdit) onEdit(response.data);
            if (refetchPathways) refetchPathways();
            form.reset();
            onOpenChange(false);
        } catch (error) {
            console.error("Failed to update pathway:", error);
            toast.error("Failed to update pathway. See console.");
        } finally {
            setIsSubmitting(false);
        }
    }    

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-(--neutral-900)">Edit Pathway</DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">Update pathway details.</DialogDescription>
                </DialogHeader>
                <div>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="edit-pathway-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="title"
                                    render={({ field, fieldState }) => (
                                        <Field>
                                            <FieldLabel>Pathway Title *</FieldLabel>
                                            <FieldContent>
                                                <Input
                                                    {...field}
                                                    placeholder="Enter pathway title"
                                                    className={cn(
                                                        "border border-(--neutral-300) focus:ring-0 text-sm font-body",
                                                        fieldState.error &&
                                                        "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                    )}
                                                />
                                                <FieldError
                                                    errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                                />
                                            </FieldContent>
                                        </Field>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="levelId"
                                    render={({ field, fieldState }) => (
                                        <Field>
                                            <FieldLabel>Pathway Type *</FieldLabel>
                                            <FieldContent>
                                                <Select
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                        const selected = pathwayTypes.find((pt) => String(pt.id) === String(value));
                                                        form.setValue("level", selected?.name || "", { shouldValidate: true });
                                                    }}
                                                    value={field.value}
                                                >
                                                    <SelectTrigger
                                                        className={cn(
                                                            "w-full",
                                                            fieldState.error &&
                                                            "border-(--color-destructive) focus:ring-(--color-primary)"
                                                        )}
                                                    >
                                                        <SelectValue
                                                            placeholder={isLoadingPathwayTypes ? "Loading pathway types..." : "Select pathway type"}
                                                            className="font-body"
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {pathwayTypes.map((pt) => (
                                                            <SelectItem key={pt.id ?? pt.name} value={String(pt.id)} className="font-body">
                                                                {pt.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FieldError
                                                    errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                                />
                                            </FieldContent>
                                        </Field>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name="courses"
                                render={({ field, fieldState }) => (
                                    <Field>
                                        <FieldLabel>Associated Courses *</FieldLabel>
                                        <FieldContent>
                                            <Select
                                                onValueChange={(value) => {
                                                    if (value && !((field.value ?? []).includes(value))) {
                                                        const next = [...(field.value ?? []), value];
                                                        field.onChange(next);
                                                        form.clearErrors("courses");
                                                    }
                                                }}
                                                value=""
                                                disabled={isLoadingCourses}
                                            >
                                                <SelectTrigger
                                                    className={cn(
                                                        "select-trigger mt-1 h-10 w-full font-body overflow-hidden [&_[data-slot=select-value]]:truncate",
                                                        fieldState.error &&
                                                        "border-(--color-destructive) focus:ring-(--color-primary)"
                                                    )}
                                                >
                                                    <SelectValue placeholder={isLoadingCourses ? "Loading courses..." : "Select all that apply"} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {courses.map((course) => (
                                                        <SelectItem key={course.id} value={String(course.id)} className="font-body">
                                                            {course.title}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {(field.value ?? []).length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {(field.value ?? []).map((courseId) => {
                                                        const course = courses.find(c => c.id === Number(courseId));
                                                        return (
                                                            <div key={courseId} className="tag-chip">
                                                                <span className="font-body">{course ? course.title : courseId}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const next = (field.value ?? []).filter((id) => id !== courseId);
                                                                        field.onChange(next);
                                                                        if (next.length === 0) {
                                                                            form.setError("courses", { type: "manual", message: "This is required" });
                                                                        } else {
                                                                            form.clearErrors("courses");
                                                                        }
                                                                    }}
                                                                    className="ml-1 text-blue hover:text-blue/70"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            <FieldError
                                                errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                            />
                                        </FieldContent>
                                    </Field>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field, fieldState }) => (
                                    <Field>
                                        <FieldLabel>Description *</FieldLabel>
                                        <FieldContent>
                                            <Textarea
                                                {...field}
                                                rows={4}
                                                className={cn(
                                                    "min-h-30 resize-none border border-(--neutral-300) font-body",
                                                    fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                )}
                                                placeholder="Describe what this pathway covers..."
                                            />
                                            <FieldError
                                                errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                            />
                                        </FieldContent>
                                    </Field>
                                )}
                            />
                        </form>
                    </Form>
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant=""
                        form="edit-pathway-form"
                        disabled={isSubmitting || !canSubmit}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center w-full">
                                <ClipLoader size={20} color="#fff" />
                            </span>
                        ) : (
                            "Save Changes"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default EditPathwayDialog
